import DiffMatchPatch from 'diff-match-patch';
import textStatistics from 'text-statistics';
import Sentiment from 'sentiment';
import { useStoreState } from 'pullstate';
import { useEffect } from 'react';
import { useState } from 'react';
import { Text1Scan } from '../../stores/compareScans/Text1Scan';
import { Text2Scan } from '../../stores/compareScans/Text2Scan';
import UploadDoc from '../transitions/UploadDoc';
import { calculateContentAnalytics, estimateReadingTime, findTop3RepeatedWords } from './helpers';
import SentimentResult from './SentimentResult';

import ReplayIcon from '@mui/icons-material/Replay';
import { Button, Container, Typography, Box, List, ListItem, ListItemText } from '@mui/material';

const dmp = new DiffMatchPatch();

const Compare = () => {
    const { content: text1Content } = useStoreState(Text1Scan);
    const { content: text2Content } = useStoreState(Text2Scan);

    const [text1, setText1] = useState(text1Content);
    const [text2, setText2] = useState(text2Content);

    const [text1ReadabilityScore, setText1ReadabilityScore] = useState(null);
    const [text2ReadabilityScore, setText2ReadabilityScore] = useState(null);

    const [text1SentimentResult, setText1SentimentResult] = useState(null);
    const [text2SentimentResult, setText2SentimentResult] = useState(null);

    const [text1TopWords, setText1TopWords] = useState([]);
    const [text2TopWords, setText2TopWords] = useState([]);

    const [text1Analytics, setText1Analytics] = useState(null);
    const [text2Analytics, setText2Analytics] = useState(null);

    const [similarity, setSimilarity] = useState(null);
    const [error, setError] = useState(null);

    const [step, setStep] = useState(1);

    const handleNextStep = () => {
        if (step < 3) {
            if (step === 2 && text1.trim() === '') {
                setError('Text/Doc 1 is required.');
                return;
            }
            if (step === 3 && text2.trim() === '') {
                setError('Text/Doc 2 is required.');
                return;
            }
            setStep(step + 1);
            setError(null);
        }
    };

    const handlePreviousStep = () => {
        if (step === 1) {
            return;
        }
        if (step === 2) {
            setText1('');
            setStep(1);
            Text1Scan.update((s) => {
                s.content = '';
            });
        } else if (step === 3) {
            setText2('');
            setStep(2);
            setError(null);
            Text2Scan.update((s) => {
                s.content = '';
            });
        }
    };

    const handleReset = () => {
        setText1('');
        setText2('');
        setSimilarity(null);
        setError(null);
        setStep(1);
        Text1Scan.update((s) => {
            s.content = '';
        });
        Text2Scan.update((s) => {
            s.content = '';
        });

        setText1ReadabilityScore(null);
        setText2ReadabilityScore(null);
        setText1TopWords([]);
        setText2TopWords([]);
        setText1SentimentResult(null);
        setText2SentimentResult(null);
        setText1Analytics(null);
        setText2Analytics(null);
    };

    useEffect(() => {
        if (text1Content) {
            setText1(text1Content);

            const editorDiv = document.getElementById('text-editor-1');
            if (editorDiv) {
                editorDiv.innerHTML = text1Content;
            }
        }
    }, [text1Content]);
    useEffect(() => {
        if (text2Content) {
            setText2(text2Content);
            const editorDiv = document.getElementById('text-editor-2');
            if (editorDiv) {
                editorDiv.innerHTML = text2Content;
            }
        }
    }, [text2Content]);

    const handleSubmit = () => {
        if (step === 3) {
            const diffs = dmp.diff_main(text1, text2);
            dmp.diff_cleanupSemantic(diffs);

            // Calculate similarity
            let commonTextLength = 0;
            diffs.forEach((diff) => {
                if (diff[0] === 0) commonTextLength += diff[1].length;
            });

            const fleschKincaid1 = new textStatistics(text1).fleschKincaidReadingEase();
            setText1ReadabilityScore(fleschKincaid1);

            const sentiment1 = new Sentiment();
            const result1 = sentiment1.analyze(text1);
            setText1SentimentResult(result1);

            const fleschKincaid2 = new textStatistics(text2).fleschKincaidReadingEase();
            setText2ReadabilityScore(fleschKincaid2);

            const sentiment2 = new Sentiment();
            const result2 = sentiment2.analyze(text2);
            setText2SentimentResult(result2);

            const topWords1 = findTop3RepeatedWords(text1);
            setText1TopWords(topWords1);
            const topWords2 = findTop3RepeatedWords(text2);
            setText2TopWords(topWords2);

            const text1Analytics = calculateContentAnalytics(text1);
            setText1Analytics(text1Analytics);

            const text2Analytics = calculateContentAnalytics(text2);
            setText2Analytics(text2Analytics);

            setSimilarity(commonTextLength / ((text1.length + text2.length) / 2));
            setStep(4); // Move to Step 4 (result) after submission
            setError(null);
        }
    };

    return (
        <Container sx={{ width: '100%', py: '20px' }}>
            {step === 1 && (
                <>
                    <Typography variant='h6'>Step 1: Introduction</Typography>
                    Provide an introduction if needed.
                    <Box display='flex' alignItems='center' gap={1}>
                        <Button
                            onClick={handleNextStep}
                            disabled={step !== 1 && !text1.trim()}
                            variant='contained'
                            sx={{
                                backgroundColor: '#00cc66',
                                color: '#000',
                                '&:hover': {
                                    backgroundColor: '#00cc44',
                                },
                                '&:disabled': {
                                    backgroundColor: '#ccc',
                                    cursor: 'not-allowed',
                                },
                            }}
                        >
                            Next
                        </Button>
                    </Box>
                </>
            )}

            <Box
                sx={{
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                }}
            >
                {step === 2 && (
                    <>
                        <Typography variant='h6'>Step 2: Text/Doc 1</Typography>

                        {text1.length < 1 && <UploadDoc CurrentScan={Text1Scan} />}
                        <Box
                            style={{ height: '250px' }}
                            contentEditable
                            id='text-editor-1'
                            onInput={(e) => {
                                e.stopPropagation();
                                const text1 = e.target.innerText;
                                setText1(text1);
                                setError(null);
                            }}
                            onPaste={(e) => {
                                e.preventDefault();
                                const pastedText = e.clipboardData.getData('text/plain');
                                document.execCommand('insertText', false, pastedText);
                            }}
                        />
                        <Box display='flex' alignItems='center' gap={1} mt={3}>
                            <Button
                                onClick={handlePreviousStep}
                                variant='contained'
                                sx={{
                                    color: 'black',
                                    backgroundColor: 'rgb(48, 188, 237)',
                                    '&:hover': {
                                        backgroundColor: 'rgb(36, 175, 230)',
                                    },
                                }}
                            >
                                Previous
                            </Button>

                            <Button
                                onClick={handleNextStep}
                                disabled={step !== 1 && !text1.trim()}
                                variant='contained'
                                sx={{
                                    backgroundColor: '#00cc66',
                                    color: '#000',
                                    '&:hover': {
                                        backgroundColor: '#00cc44',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#ccc',
                                        cursor: 'not-allowed',
                                    },
                                }}
                            >
                                Next
                            </Button>
                        </Box>
                    </>
                )}

                {step === 3 && (
                    <>
                        <Typography variant='h6'>Step 3: Text/Doc 2</Typography>
                        {text2.length < 1 && <UploadDoc CurrentScan={Text2Scan} />}
                        <Box
                            style={{ height: '250px' }}
                            contentEditable
                            id='text-editor-2'
                            onInput={(e) => {
                                e.stopPropagation();
                                const text2 = e.target.innerText;
                                setText2(text2);
                                setError(null);
                            }}
                            onPaste={(e) => {
                                e.preventDefault();
                                const pastedText = e.clipboardData.getData('text/plain');
                                document.execCommand('insertText', false, pastedText);
                            }}
                        />
                        <Box display='flex' alignItems='center' gap={1} mt={3}>
                            <Button
                                onClick={handlePreviousStep}
                                variant='contained'
                                sx={{
                                    color: 'black',
                                    backgroundColor: 'rgb(48, 188, 237)',
                                    '&:hover': {
                                        backgroundColor: 'rgb(36, 175, 230)',
                                    },
                                }}
                            >
                                Previous
                            </Button>

                            <Button
                                onClick={handleSubmit}
                                disabled={!text2.trim()}
                                type='submit'
                                variant='contained'
                                sx={{
                                    color: 'rgba(0, 0, 0, 0.87)',
                                    backgroundColor: 'rgb(255, 193, 7)',
                                    '&:hover': {
                                        backgroundColor: 'rgb(178, 135, 4)',
                                    },
                                    '&:disabled': {
                                        backgroundColor: '#ccc',
                                        cursor: 'not-allowed',
                                    },
                                }}
                            >
                                Submit
                            </Button>
                        </Box>
                    </>
                )}
            </Box>
            {step === 4 && (
                <Box>
                    <Typography variant='h6'>Step 4: Results</Typography>
                    {similarity !== null && <p>Similarity: {(similarity * 100).toFixed(2)}%</p>}

                    <Box
                        display='flex'
                        flexDirection={{ xs: 'column', sm: 'row' }}
                        sx={{ width: '100%' }}
                    >
                        <Box
                            sx={{
                                width: { xs: '100%', sm: '50%' },
                                padding: '10px',
                                border: '1px solid #ccc',
                                margin: { xs: '10px 0', sm: '10px' },
                                borderRadius: '5px',
                                backgroundColor: '#f9f9f9',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                            }}
                        >
                            {text1 && (
                                <Box>
                                    <Typography variant='subtitle1'>
                                        Text1 Content Analytics:
                                    </Typography>
                                    <>Word Count: {text1Analytics.wordCount}</>
                                    <>Character Count: {text1Analytics.characterCount}</>
                                    <>
                                        Average Word Length:{' '}
                                        {text1Analytics.averageWordLength.toFixed(2)}
                                    </>

                                    <>
                                        Estimated reading time for Text 1:{' '}
                                        {estimateReadingTime(text1)} minutes
                                    </>

                                    {text1ReadabilityScore !== null && (
                                        <>
                                            Text1 Flesch-Kincaid Readability score:{' '}
                                            {text1ReadabilityScore}
                                        </>
                                    )}
                                </Box>
                            )}

                            {text1 && (
                                <Box>
                                    <h3>Text1 Top 3 Most Repeated Words:</h3>
                                    <List>
                                        {text1TopWords?.map((wordData, index) => (
                                            <ListItemText key={index}>
                                                {wordData.word} ({wordData.frequency} times)
                                            </ListItemText>
                                        ))}
                                    </List>
                                </Box>
                            )}

                            {text1SentimentResult && (
                                <SentimentResult
                                    title='Text1 Sentiment Analysis Result'
                                    sentimentResult={text1SentimentResult}
                                />
                            )}
                        </Box>

                        <Box
                            sx={{
                                width: { xs: '100%', sm: '50%' },
                                padding: '10px',
                                border: '1px solid #ccc',
                                margin: { xs: '10px 0', sm: '10px' },
                                borderRadius: '5px',
                                backgroundColor: '#f9f9f9',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '16px',
                            }}
                        >
                            {text2 && (
                                <Box>
                                    <h3>Text2 Content Analytics:</h3>
                                    <p>Word Count: {text2Analytics.wordCount}</p>
                                    <p>Character Count: {text2Analytics.characterCount}</p>
                                    <p>
                                        Average Word Length:{' '}
                                        {text2Analytics.averageWordLength.toFixed(2)}
                                    </p>
                                    <p>
                                        Estimated reading time for Text 2:{' '}
                                        {estimateReadingTime(text2)} minutes
                                    </p>

                                    {text2ReadabilityScore !== null && (
                                        <p>
                                            Text2 Flesch-Kincaid Readability score:{' '}
                                            {text2ReadabilityScore}
                                        </p>
                                    )}
                                </Box>
                            )}

                            {text2 && (
                                <div>
                                    <h3>Text2 Top 3 Most Repeated Words:</h3>
                                    <List>
                                        {text2TopWords?.map((wordData, index) => (
                                            <ListItemText key={index}>
                                                {wordData.word} ({wordData.frequency} times)
                                            </ListItemText>
                                        ))}
                                    </List>
                                </div>
                            )}

                            {text2SentimentResult && (
                                <SentimentResult
                                    title='Text2 Sentiment Analysis Result'
                                    sentimentResult={text2SentimentResult}
                                />
                            )}
                        </Box>
                    </Box>

                    <Box mt={2}>
                        <Button
                            onClick={handleReset}
                            variant='contained'
                            sx={{
                                color: 'black',
                                backgroundColor: 'rgb(48, 188, 237)',
                                '&:hover': {
                                    boxShadow:
                                        '0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)',
                                },
                            }}
                            startIcon={<ReplayIcon />}
                        >
                            Clear
                        </Button>
                    </Box>
                </Box>
            )}

            {error && (
                <Typography variant='h6' my={2} fontWeight={600} color='error'>
                    An Error occurred: {error}
                </Typography>
            )}
        </Container>
    );
};

export default Compare;
